import React, { useState, useRef } from 'react';
import { Lock, Mail, Phone, Eye, EyeOff, ShieldCheck, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  changePasswordSchema,
  createPasswordSchema,
  validateForm,
} from '../../lib/validations';

/**
 * Admin "Login & Password" screen (dashboard tab).
 *
 * Adapts to the account's state:
 * - Has a password  → CHANGE mode: current + new + confirm.
 * - No password yet (a phone-OTP user promoted to admin) → CREATE mode:
 *   new + confirm, plus an email field when the account has none (email is
 *   the login identifier). Phone-OTP login keeps working either way.
 */
const AccountSecurity = () => {
  const { user, setPassword, refreshUser } = useAuth();

  const hasPassword = !!user?.hasPassword;
  const hasEmail = !!user?.email;

  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    email: '',
  });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Synchronous in-flight guard: state updates lag a render, so a rapid
  // double-click/Enter could fire two requests before `isSubmitting` (and the
  // disabled button) take effect. A ref flips immediately to block that.
  const submittingRef = useRef(false);

  const setField = (name) => (e) => {
    setForm((f) => ({ ...f, [name]: e.target.value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };
  const toggle = (name) => () => setShow((s) => ({ ...s, [name]: !s[name] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return; // ignore repeat submits while one is in flight

    // Password rules (min 8 + complexity + confirm-match) via the shared schema.
    const schema = hasPassword ? changePasswordSchema : createPasswordSchema;
    const payloadToValidate = hasPassword
      ? {
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
          confirmNewPassword: form.confirmNewPassword,
        }
      : {
          newPassword: form.newPassword,
          confirmNewPassword: form.confirmNewPassword,
        };

    const result = validateForm(schema, payloadToValidate);
    const nextErrors = result.success ? {} : { ...result.errors };

    // Email is required (and must look valid) only when the account has none yet.
    const needsEmail = !hasEmail;
    const emailValue = form.email.trim();
    if (needsEmail) {
      if (!emailValue) {
        nextErrors.email = 'Email is required to enable email + password login';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
        nextErrors.email = 'Please enter a valid email address';
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const payload = { newPassword: form.newPassword };
      if (hasPassword) payload.currentPassword = form.currentPassword;
      if (needsEmail) payload.email = emailValue;

      const res = await setPassword(payload);
      if (res.success) {
        toast.success(
          hasPassword
            ? 'Password updated successfully.'
            : 'Password created — you can now sign in with your email and password.'
        );
        setForm({ currentPassword: '', newPassword: '', confirmNewPassword: '', email: '' });
        // Refresh the user so this screen flips to "change" mode and shows the new email.
        await refreshUser();
      } else {
        toast.error(res.error || 'Failed to save password.');
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const inputBase =
    'w-full pl-12 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all';
  const passwordsMismatch =
    form.confirmNewPassword && form.confirmNewPassword !== form.newPassword;

  return (
    <div className="max-w-xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[#548B2E]" />
          Login &amp; Password
        </h1>
        <p className="text-gray-600 text-sm sm:text-base">
          {hasPassword
            ? 'Change the password you use to sign in with your email.'
            : 'Create a password to sign in with your email, in addition to phone OTP.'}
        </p>
      </div>

      {/* Sign-in methods summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex gap-3">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900 space-y-1">
          {user?.phone && (
            <p className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Phone OTP sign-in is active for <strong>{user.phone}</strong>.
            </p>
          )}
          <p className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            {hasPassword
              ? <>Email sign-in is active for <strong>{user?.email}</strong>.</>
              : hasEmail
                ? <>Add a password below to enable email sign-in for <strong>{user?.email}</strong>.</>
                : <>Add an email and password below to enable email sign-in.</>}
          </p>
          {!hasPassword && (
            <p className="text-blue-700/90">
              Setting a password won&apos;t affect your phone OTP login — you&apos;ll be able to use both.
            </p>
          )}
        </div>
      </div>

      {/* Form card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {/* Email — read-only when present, required input when missing */}
          {hasEmail ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Login Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={setField('email')}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className={`${inputBase} ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>
          )}

          {/* Current password — only when a password already exists */}
          {hasPassword && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={show.current ? 'text' : 'password'}
                  value={form.currentPassword}
                  onChange={setField('currentPassword')}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  className={`${inputBase} ${errors.currentPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                />
                <button type="button" onClick={toggle('current')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {show.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.currentPassword && <p className="mt-1 text-xs text-red-500">{errors.currentPassword}</p>}
            </div>
          )}

          {/* New password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {hasPassword ? 'New Password' : 'Password'}
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={show.next ? 'text' : 'password'}
                value={form.newPassword}
                onChange={setField('newPassword')}
                placeholder="Min. 8 chars, incl. upper, lower & number"
                autoComplete="new-password"
                className={`${inputBase} ${errors.newPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
              />
              <button type="button" onClick={toggle('next')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {show.next ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.newPassword && <p className="mt-1 text-xs text-red-500">{errors.newPassword}</p>}
          </div>

          {/* Confirm new password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={show.confirm ? 'text' : 'password'}
                value={form.confirmNewPassword}
                onChange={setField('confirmNewPassword')}
                placeholder="Re-enter password"
                autoComplete="new-password"
                className={`${inputBase} ${
                  passwordsMismatch || errors.confirmNewPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              <button type="button" onClick={toggle('confirm')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {show.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {passwordsMismatch ? (
              <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
            ) : errors.confirmNewPassword ? (
              <p className="mt-1 text-xs text-red-500">{errors.confirmNewPassword}</p>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              hasPassword ? 'Update Password' : 'Create Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AccountSecurity;
